import { signedApiClient } from './apiClient';
import { UserModel, type IUserPermissions, type NetworkUser } from '@/models/user';
import type { FormModel } from '@/models/form';

class StudyUsersService {

  private investigationEndpoint: string = '';
  private studyEndpoint: string = '';

  initService(
    investigationEndpoint: string,
    studyEndpoint: string
  ) {
    this.investigationEndpoint = investigationEndpoint;
    this.studyEndpoint = studyEndpoint;
  }
  
  async getCurrentStudyUsers(identifier: string, forms: FormModel[]): Promise<UserModel[]> {
    const uri = `${this.investigationEndpoint}/${identifier}/users`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkCurrentUsers: NetworkUser[] = endpointResponse.data;

    return networkCurrentUsers.map(user => {
      user.status = 'Activated';
      this.parseFormsPermissions(user, forms);
      user.permissionsList = this.parsePermissions(user.permissions as IUserPermissions);
      return UserModel.mapFromBackend(user, '', forms);
    });
  }

  async getPendingStudyUsers(identifier: string, forms: FormModel[]): Promise<UserModel[]> {
    const uri = `${this.investigationEndpoint}/${identifier}/pending-users`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkPendingUsers: NetworkUser[] = endpointResponse.data;

    return networkPendingUsers.map(user => {
      user.status = 'Pending';
      user.identifier = user.inviteIdentifier;
      this.parseFormsPermissions(user, forms);
      user.permissionsList = this.parsePermissions(user.permissions as IUserPermissions);
      return UserModel.mapFromBackend(user, '', forms);
    });
  }

  parsePermissions(userPermissions: IUserPermissions) {
    const permissions: string[] = [];
    Object.entries(userPermissions).forEach(([key, value]) => {
      const typedKey = key as keyof IUserPermissions;
      if (typedKey === 'forms') {
        if (Array.isArray(value)) {
          permissions.push(`View forms: ${value.length} assigned`);
        } else {
          permissions.push('View forms: All assigned');
        }
      } else if (typedKey === 'editForms') {
        if (Array.isArray(value)) {
          permissions.push(`Add entries: ${value.length} assigned`);
        } else {
          permissions.push('Add entries: All assigned');
        }
      } else if (typedKey === 'viewEntries') {
        if (value) {
          permissions.push('View entries: All entries');
        } else {
          permissions.push('View entries: Own entries');
        }
      } else if (typedKey === 'export') {
        if (value) {
          permissions.push('Export data is allowed');
        } else {
          permissions.push('Export data is forbidden');
        }
      } else if (typedKey === 'manageInvest') {
        if (value) {
          permissions.push('Manage study is allowed');
        } else {
          permissions.push('Manage study is forbidden');
        }
      } else if (typedKey === 'createStudy') {
        if (value) {
          permissions.push('Create study is allowed');
        } else {
          permissions.push('Create study is forbidden');
        }
      } else if (typedKey === 'editCases') {
        if (value) {
          permissions.push('Edit cases is allowed');
        } else {
          permissions.push('Edit cases is forbidden');
        }
      }
    });
    return permissions;
  }

  parseFormsPermissions(user: NetworkUser, forms: FormModel[]) {
    user.formsPermissions =
    (user.permissions as IUserPermissions).forms === true ? 
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).forms as string[];
    user.editFormsPermissions =
    (user.permissions as IUserPermissions).editForms === true ? 
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).editForms as string[];
  }

  parseFormsPermissionsForUser(user: UserModel, forms: FormModel[]) {
    user.formsPermissions =
    (user.permissions as IUserPermissions).forms === true ? 
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).forms as string[];
    user.editFormsPermissions =
    (user.permissions as IUserPermissions).editForms === true ? 
      forms.map(form => form.base64Id as string) :
      (user.permissions as IUserPermissions).editForms as string[];
  }

  async removeUserFromStudy(identifier: string, userIdentifier: string) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }

  async inviteUserToStudy(studyId: string, invites: UserModel[], groupId?: string) {
    const uri = `${this.investigationEndpoint}/${studyId}/invites`;
    const apiClient = signedApiClient('POST', uri);

    const payloadInvites: unknown[] = [];
    invites.forEach(invite => {
      payloadInvites.push({
        email: invite.email.toLowerCase(),
        groupIdentifier: groupId,
        viewPermissions: invite.permissions.viewEntries ? groupId ? 'groupEntries' : 'allEntries' : 'ownEntries',
        editCases: invite.permissions.editCases,
        exportPermissions: invite.permissions.export,
        manageInvestPermissions: invite.permissions.manageInvest,
        forms: invite.permissions.forms,
        editForms: invite.permissions.editForms,
        createStudy: invite.permissions.createStudy,
        notificationProfile: invite.permissions.notificationProfile
      });
    });
    
    const response = await apiClient.post(uri, {
      invites: payloadInvites
    });
    return response.data;
  }

  async patchInvitePermissions(studyId: string, inviteIdentifier: string, permissions: IUserPermissions) {
    const uri = `${this.investigationEndpoint}/${studyId}/invite/${inviteIdentifier}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, permissions);
  }
}

export default new StudyUsersService();
